import { Field, FieldArray, useFormikContext } from 'formik';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SquareFormValues } from '@/utils/forms/squareTransformers';
import { getPeriodTemplates, type PeriodTemplate } from '@/utils/squares/periodTemplates';
import { useEffect, useMemo } from 'react';
import { Trash2 } from 'lucide-react';

interface PeriodsSectionProps {
  disabled?: boolean;
}

export function PeriodsSection({ disabled = false }: PeriodsSectionProps) {
  const { values, setFieldValue } = useFormikContext<SquareFormValues>();

  // Calculate total prize from period prizes
  const totalPeriodPrizes = useMemo(() => {
    if (!values.periods || values.periods.length === 0) return 0;
    return (values.periods as PeriodTemplate[]).reduce((sum, period) => {
      const prize =
        typeof period.prizeAmount === 'number'
          ? period.prizeAmount
          : parseFloat(String(period.prizeAmount) || '0');
      return sum + (isNaN(prize) ? 0 : prize);
    }, 0);
  }, [values.periods]);

  // Auto-populate or update periods when sport changes (e.g. NBA/NFL → 4 quarters, NHL → 3 periods, NCAAMB → Halftime + Final)
  useEffect(() => {
    if (values.sport) {
      const templates = getPeriodTemplates(values.sport);
      if (templates.length > 0) {
        const currentPeriods = (values.periods || []) as PeriodTemplate[];
        const currentPeriodCodes = currentPeriods.map(p => p.periodCode).join(',');
        const expectedPeriodCodes = templates.map(t => t.periodCode).join(',');
        // Replace periods when empty or when they don't match the sport's structure
        if (currentPeriodCodes !== expectedPeriodCodes) {
          setFieldValue('periods', templates);
          if (!values.total_prize_amount) {
            setFieldValue('total_prize_amount', 0);
          }
        }
      }
    }
  }, [values.sport, values.periods, values.total_prize_amount, setFieldValue]);

  // Auto-update total prize amount whenever period prizes change
  useEffect(() => {
    if (values.periods && values.periods.length > 0) {
      // Only update if the calculated total is different from current total
      // This prevents infinite loops while still updating when prizes change
      const currentTotal = values.total_prize_amount || 0;
      if (Math.abs(totalPeriodPrizes - currentTotal) > 0.01) {
        setFieldValue('total_prize_amount', totalPeriodPrizes);
      }
    }
  }, [totalPeriodPrizes, values.periods, values.total_prize_amount, setFieldValue]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Periods & Prizes</h3>
          <p className="text-sm text-muted-foreground">
            Configure scoring periods and prize distribution. Periods are auto-populated based on
            sport. Set prize amounts for each period, and the total prize will be calculated
            automatically.
          </p>
        </div>
      </div>

      <FieldArray name="periods">
        {({ push: _push, remove, form }) => {
          const periods = (form.values.periods || []) as PeriodTemplate[];

          return (
            <div className="space-y-4">
              {periods.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Select a sport to auto-populate periods</p>
                </div>
              ) : (
                periods.map((period, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 grid grid-cols-3 gap-4">
                        <div className="flex flex-col gap-2">
                          <Label>Period Code</Label>
                          <Field
                            as={Input}
                            name={`periods.${index}.periodCode`}
                            value={period.periodCode}
                            disabled={true}
                            className="bg-gray-50"
                          />
                          <p className="text-xs text-muted-foreground">Auto-set based on sport</p>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label>Label *</Label>
                          <Field
                            as={Input}
                            name={`periods.${index}.label`}
                            placeholder="e.g., Q1, Halftime"
                            disabled={disabled || period.periodCode === 'FINAL'}
                          />
                          {period.periodCode === 'FINAL' && (
                            <p className="text-xs text-muted-foreground">Final cannot be changed</p>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label>Prize Amount ($) *</Label>
                          <Field
                            as={Input}
                            name={`periods.${index}.prizeAmount`}
                            type="number"
                            min="0"
                            step="any"
                            disabled={disabled}
                            // Prevent the scroll wheel from changing the value
                            // when the user is just scrolling the page with the
                            // input focused. Blurring on wheel also removes the
                            // native up/down stepper hover state.
                            onWheel={(e: React.WheelEvent<HTMLInputElement>) =>
                              e.currentTarget.blur()
                            }
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const value = parseFloat(e.target.value) || 0;
                              setFieldValue(`periods.${index}.prizeAmount`, value);
                            }}
                          />
                          <p className="text-xs text-muted-foreground">
                            Total prize will update automatically
                          </p>
                        </div>
                      </div>

                      {period.periodCode !== 'FINAL' && periods.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          disabled={disabled}
                          className="ml-4"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      End Condition: {period.endCondition} | Order: {period.displayOrder}
                    </div>
                  </div>
                ))
              )}

              {periods.length > 0 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm font-semibold">
                    <strong>Total Prize Amount (Auto-calculated):</strong> $
                    {totalPeriodPrizes.toFixed(2)}
                  </div>
                  <div className="text-xs text-muted-foreground">Sum of all period prizes</div>
                </div>
              )}
            </div>
          );
        }}
      </FieldArray>
    </div>
  );
}
